/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import classnames from 'classnames';

import IconEarthWorld from './icons/IconEarthWorld';
import { colorClassNames } from '../styles/Colors';
import { LanguageWithCountryCodeOption, LanguageCodeOnly, LanguageWithCountryCode } from '../localization/LocalizedLanguage';

import { useActiveUser } from '../hooks/useUser';
import { useLists } from '../hooks/useLists';

interface Props {
  htmlId: string;
  localeList: any[];
  value: any;
  onChange(d): void;
  onBlur?(): void;
  onFocus?(): void;
  color?: string;
  size?: any;
  showSelectedFullName?: boolean;
  className?: string;
}
const LanguageDropdownList: React.FC<Props> = (props) => {
  const { localeList, value, className, showSelectedFullName, htmlId, onFocus, onBlur, onChange } = props;
  const color = props.color || 'dark-neutral';
  const size = props.size || '24px';

  const selectedLocale = localeList.filter((c) => c.id === value)[0];
  const optionList = localeList.map((v) => <LanguageWithCountryCodeOption key={v.id} locale={v} isDisabled={false} />);

  if (!selectedLocale) { return null; }

  return (
    <div className={classnames('cmp-languagedropdownlist', className)}>
      <div className="cmp-languagedropdownlist--content">
        <div className={`language-code ${colorClassNames.font(color)}`}>
          { showSelectedFullName
            ? <LanguageWithCountryCode locale={selectedLocale} />
            : <LanguageCodeOnly locale={selectedLocale} />
          }
        </div>
        <select
          name={htmlId}
          id={htmlId}
          value={value}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={(e) => {
            const id = parseInt(e.target.value, 10);
            const localeData = localeList.filter(l => l.id === id)[0];
            onChange(localeData);
          }}
        >
          {optionList}
        </select>
      </div>

      <label htmlFor={htmlId} className="cmp-languagedropdownlist--label">
        <IconEarthWorld size={size} color={color} />
      </label>
    </div>
  );
};

// LargeLanguageDropdownList
// has larger font size and hides the fullName based on screen width
export const LargeLanguageDropdownList: React.FC<Props> = (props) => (
  <LanguageDropdownList {...props} className="large-responsive" showSelectedFullName size={36} />
);

interface PreferredLanguageProps {
  htmlId: string;
  onBlur?(): void;
  onFocus?(): void;
  color?: string;
  size?: any;
  showSelectedFullName?: boolean;
}

export const PreferredLanguageDropdownList: React.FC<PreferredLanguageProps> = (props) => {
  const [{ user }, userActions] = useActiveUser();
  const localeId = user.locale_id || -1;
  const [{ localeList }] = useLists();

  return (
    <LanguageDropdownList
      {...props}
      localeList={localeList}
      value={localeId}
      onChange={(localeData) => {
        userActions.setLocale(localeData);
      }}
    />
  );
};

export const LargePreferredLanguageDropdownList: React.FC<PreferredLanguageProps> = (props) => {
  const [{ user }, userActions] = useActiveUser();
  const localeId = user.locale_id || -1;
  const [{ localeList }] = useLists();

  return (
    <LargeLanguageDropdownList
      {...props}
      localeList={localeList}
      value={localeId}
      onChange={(localeData) => {
        userActions.setLocale(localeData);
      }}
    />
  );
};

interface MedliLanguageProps {
  htmlId: string;
  value: string;
  onChange(d): void;
  onBlur?(): void;
  onFocus?(): void;
  color?: string;
  size?: any;
}
export const MedliLanguageDropdownList: React.FC<MedliLanguageProps> = (props) => {
  const [{ localeMedliList }] = useLists();

  return (
    <LanguageDropdownList
      {...props}
      localeList={localeMedliList}
    />
  );
};

export const NestedLanguageDropdownList: React.FC<MedliLanguageProps> = (props) => {
  const [{ localeList }] = useLists();

  return (
    <LanguageDropdownList
      {...props}
      localeList={localeList}
    />
  );
};

export default LanguageDropdownList;
